/**
 * useGettingStarted — GET /api/t/{slug}/getting-started.
 *
 * Paying-tenant first-run onboarding. The backend returns the checklist
 * state plus a server-computed `first_run` gate (true only for a provisioned
 * PAID, non-demo tenant whose checklist is neither dismissed nor complete).
 * The frontend mounts the welcome moment + getting-started checklist ONLY when
 * `first_run` is true — demo tenants and non-provisioned tenants never see it.
 *
 * `completed` maps the four first-run steps:
 *   (a) create_first_jd          → "Create your first job"
 *   (b) upload_first_resume OR connect_greenhouse → "Add candidates / import"
 *   (c) run_first_evaluation     → "Run your first screen"
 * (invite_first_teammate is tracked but not a first-run gate step.)
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";

// Module-local: only referenced by GettingStartedState below (ts-prune).
type GettingStartedItem =
  | "run_first_evaluation"
  | "create_first_jd"
  | "upload_first_resume"
  | "invite_first_teammate"
  | "connect_greenhouse";

export interface GettingStartedState {
  /** ISO timestamp per item, or null if not yet completed. */
  completed: Record<GettingStartedItem, string | null>;
  /** ISO timestamp when the checklist was dismissed, or null. */
  dismissed_at: string | null;
  /** Server-computed: show the first-run onboarding UX iff true. */
  first_run: boolean;
  /** Demo tenants are excluded from first-run (their own seeded experience). */
  is_demo: boolean;
}

export function gettingStartedKey(tenantSlug: string | undefined) {
  return ["getting-started", tenantSlug] as const;
}

export function useGettingStarted(): UseQueryResult<GettingStartedState, ApiError> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  return useQuery<GettingStartedState, ApiError>({
    queryKey: gettingStartedKey(tenantSlug),
    queryFn: async () => {
      const resp = await apiClient.get<GettingStartedState>(
        `/api/t/${tenantSlug}/getting-started`,
      );
      return resp.data;
    },
    enabled: !!tenantSlug,
    staleTime: 60_000,
  });
}
